import React from 'react';
import { Button, Input, Spin } from 'antd';
import {
    ArrowLeftOutlined,
    DownOutlined,
    UpOutlined,
    RightOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    MobileOutlined,
    LockOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { LockDto } from 'services/locks/locks.service';
import { EntityPicker, PickerItem } from 'components/entity-picker/entity-picker';
import { useKeyAccessController } from './key-access.controller';
import { KeyAccessFragmentProps } from './interfaces';
import { DAY_BITS, VigenciaPreset } from './key-schedule';
import 'fragments/keys/fragments/key-access/key-access.scss';

export const KeyAccessFragment: React.FC<KeyAccessFragmentProps> = ({ useController = useKeyAccessController }) => {
    const { translate } = useTranslator();
    const t = (key: string) => translate({ key });
    const c = useController();

    // Se define antes de las ramas tempranas: cargando, error de carga y sin
    // cerraduras la necesitan igual que el formulario. Sin esto, quien entra a
    // un sitio sin cerraduras (o con la carga colgada/fallada) queda encerrado
    // — el webview no tiene chrome nativo de vuelta.
    const nav = (
        <div className={'ka-nav'}>
            <button className={'ka-back'} aria-label={t('key-access.back')} onClick={c.onClose}>
                <ArrowLeftOutlined aria-hidden />
            </button>
        </div>
    );

    // El endpoint de cerraduras tarda ~26s para un superadmin (1608 cerraduras, medido
    // en staging). Un estado de carga vacío convierte esa espera en una pantalla que
    // parece rota — es lo que reportó el gate en device. La pantalla vieja mostraba un
    // Spin; ésta muestra además qué está esperando.
    if (!c.locksLoaded) {
        return (
            <div className={'ka-loading'}>
                {nav}
                <div className={'ka-content'}>
                    <h1 className={'ka-title'}>{t('key-access.title')}</h1>
                    <div className={'ka-loading-body'} role={'status'}>
                        <Spin />
                        <span className={'ka-loading-text'}>{t('key-access.loading')}</span>
                    </div>
                </div>
            </div>
        );
    }

    if (c.locksLoadError) {
        return (
            <div className={'ka-state'}>
                {nav}
                <p className={'ka-state-text'}>{t('key-access.load-error')}</p>
                <Button type={'primary'} className={'ka-state-btn'} onClick={c.retryLoadLocks}>
                    {t('key-access.retry')}
                </Button>
            </div>
        );
    }

    if (c.locks.length === 0) {
        return (
            <div className={'ka-state'}>
                {nav}
                <p className={'ka-state-text'}>{t('key-access.empty')}</p>
            </div>
        );
    }

    if (c.pickerOpen) {
        const items: PickerItem[] = c.locks.map((l) => ({
            key: l.id,
            id: l.id,
            groupName: l.site_name,
            title: l.lock_name,
            subtitle: t('key-access.picker-sub'),
            icon: <LockOutlined aria-hidden />,
        }));
        return (
            <EntityPicker
                items={items}
                selectedId={c.selectedLock?.id}
                title={t('key-access.picker-title')}
                searchPlaceholder={t('key-access.picker-search')}
                backLabel={t('key-access.back')}
                emptyLabel={t('key-access.picker-no-results')}
                onSelect={(item) => c.onLockPicked(c.locks.find((l) => l.id === item.key) as LockDto)}
                onClose={c.closePicker}
            />
        );
    }

    const lockLabel = c.selectedLock ? `${c.selectedLock.site_name} · ${c.selectedLock.lock_name}` : '';

    if (c.outcome?.kind === 'created') {
        return (
            <div className={'ka'}>
                <div className={'ka-content ka-success'}>
                    <CheckCircleOutlined aria-hidden className={'ka-success-icon'} />
                    <h1 className={'ka-title'}>{t('key-access.success-title')}</h1>
                    <div className={'ka-card'}>
                        <div className={'ka-row'}>
                            <LockOutlined aria-hidden />
                            <span className={'ka-row-text ka-row-text--strong'}>{lockLabel}</span>
                        </div>
                        <div className={'ka-row'}>
                            <MobileOutlined aria-hidden />
                            <span className={'ka-row-text'}>{c.assigneeEmail}</span>
                        </div>
                        <div className={'ka-row'}>
                            <ClockCircleOutlined aria-hidden />
                            <span className={'ka-row-text'}>{c.validityLabel}</span>
                        </div>
                    </div>
                    <p className={'ka-hint'}>{t('key-access.success-body')}</p>
                    <p className={'ka-hint'}>{t('key-access.success-tip')}</p>
                </div>
                <div className={'ka-cta-bar'}>
                    <Button type={'primary'} className={'ka-cta'} onClick={c.dismissOutcome}>
                        {t('key-access.success-done')}
                    </Button>
                </div>
            </div>
        );
    }

    // Fila del botón de la puerta: idéntica en la tarjeta cerrada y en el panel
    // abierto, así que se calcula una sola vez y se reutiliza en ambas ramas.
    const lockRow = (
        <button
            className={'ka-row ka-row--action'}
            aria-label={`${t('key-access.change-lock')}: ${lockLabel}`}
            onClick={c.openPicker}
        >
            <EnvironmentOutlined aria-hidden />
            <span className={'ka-row-text ka-row-text--strong'}>{lockLabel}</span>
        </button>
    );

    return (
        <div className={'ka'}>
            <div className={'ka-nav'}>
                <button className={'ka-back'} aria-label={t('key-access.back')} onClick={c.onClose}>
                    <ArrowLeftOutlined aria-hidden />
                </button>
            </div>

            <div className={'ka-content'}>
                <h1 className={'ka-title'}>{t('key-access.title')}</h1>

                <label className={'ka-field'}>
                    <span className={'ka-label'}>
                        {t('key-access.email-label')} <span className={'ka-required'}>{t('key-access.required')}</span>
                    </span>
                    <Input
                        type={'email'}
                        size={'large'}
                        style={{ borderRadius: 8, height: 48 }}
                        placeholder={t('key-access.email-placeholder')}
                        value={c.assigneeEmail}
                        onChange={(e) => c.setAssigneeEmail(e.target.value)}
                        status={c.outcome?.kind === 'no-account' || c.outcome?.kind === 'denied' ? 'error' : undefined}
                    />
                    {(c.outcome?.kind === 'no-account' || c.outcome?.kind === 'denied') && (
                        <span className={'ka-inline-error'} role={'alert'}>
                            <ExclamationCircleOutlined aria-hidden />
                            {t(
                                c.outcome.kind === 'no-account'
                                    ? 'key-access.error-no-account'
                                    : 'key-access.error-denied',
                            )}
                        </span>
                    )}
                </label>

                {!c.panelOpen ? (
                    <div className={'ka-card'}>
                        {lockRow}
                        <div className={'ka-row'}>
                            <ClockCircleOutlined aria-hidden />
                            <span className={'ka-row-text'}>{c.validityLabel}</span>
                        </div>
                        <div className={'ka-row'}>
                            <MobileOutlined aria-hidden />
                            <span className={'ka-row-text'}>{t('key-access.delivery')}</span>
                        </div>
                        <div className={'ka-divider'} />
                        <button className={'ka-change'} onClick={c.togglePanel}>
                            {t('key-access.change')}
                            <DownOutlined aria-hidden />
                        </button>
                    </div>
                ) : (
                    <div className={'ka-card'}>
                        {lockRow}
                        <div className={'ka-divider'} />

                        <div className={'ka-group'}>
                            <span className={'ka-group-label'}>{t('key-access.validity-title')}</span>
                            <div className={'ka-chips'}>
                                {(['always', 'm1', 'm3', 'y1'] as VigenciaPreset[]).map((p) => (
                                    <button
                                        key={p}
                                        aria-pressed={c.preset === p}
                                        className={`ka-chip${c.preset === p ? ' ka-chip--on' : ''}`}
                                        onClick={() => c.setPreset(p)}
                                    >
                                        {t(`key-access.preset-${p}`)}
                                    </button>
                                ))}
                            </div>

                            {/* Presets y fechas exactas son excluyentes: `preset` es
                                un único valor, así que elegir uno descarta el otro. */}
                            {c.preset === 'exact' ? (
                                <div className={'ka-times'}>
                                    <Input
                                        type={'date'}
                                        size={'large'}
                                        aria-label={t('key-access.date-from')}
                                        style={{ borderRadius: 9, height: 44 }}
                                        value={c.startDate}
                                        onChange={(e) => c.setStartDate(e.target.value)}
                                    />
                                    <span className={'ka-times-sep'}>{t('key-access.hours-sep')}</span>
                                    <Input
                                        type={'date'}
                                        size={'large'}
                                        aria-label={t('key-access.date-to')}
                                        style={{ borderRadius: 9, height: 44 }}
                                        value={c.endDate}
                                        onChange={(e) => c.setEndDate(e.target.value)}
                                    />
                                </div>
                            ) : (
                                <button className={'ka-link'} onClick={() => c.setPreset('exact')}>
                                    {t('key-access.preset-exact')}
                                    <RightOutlined aria-hidden />
                                </button>
                            )}
                        </div>

                        <div className={'ka-divider'} />
                        <button className={'ka-change'} onClick={c.toggleRestrictions}>
                            {t('key-access.restrict')}
                            {c.restrictionsOpen ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
                        </button>

                        {c.restrictionsOpen && (
                            <>
                                <div className={'ka-group'}>
                                    <span className={'ka-group-label'}>{t('key-access.days-title')}</span>
                                    <div className={'ka-chips'}>
                                        {DAY_BITS.map((d) => (
                                            <button
                                                key={d.bit}
                                                aria-label={t(d.labelKey)}
                                                aria-pressed={(c.dow & d.bit) !== 0}
                                                className={`ka-chip ka-chip--day${c.dow & d.bit ? ' ka-chip--on' : ''}`}
                                                onClick={() => c.onToggleDay(d.bit)}
                                            >
                                                {t(d.labelKey)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className={'ka-group'}>
                                    <span className={'ka-group-label'}>{t('key-access.hours-title')}</span>
                                    <div className={'ka-times'}>
                                        {/* Keys dedicadas en vez de repetir 'hours-title' en los dos
                                            campos: con el mismo aria-label un lector de pantalla no
                                            puede distinguir el campo de inicio del de fin. */}
                                        <Input
                                            type={'time'}
                                            size={'large'}
                                            aria-label={t('key-access.hours-from')}
                                            style={{ borderRadius: 9, height: 44 }}
                                            value={c.startTime}
                                            onChange={(e) => c.setStartTime(e.target.value)}
                                        />
                                        <span className={'ka-times-sep'}>{t('key-access.hours-sep')}</span>
                                        <Input
                                            type={'time'}
                                            size={'large'}
                                            aria-label={t('key-access.hours-to')}
                                            style={{ borderRadius: 9, height: 44 }}
                                            value={c.endTime}
                                            onChange={(e) => c.setEndTime(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className={'ka-resolved'}>
                            <ClockCircleOutlined aria-hidden />
                            <span>{c.validityLabel}</span>
                        </div>

                        <div className={'ka-divider'} />
                        <button className={'ka-change'} onClick={c.togglePanel}>
                            {t('key-access.done')}
                            <UpOutlined aria-hidden />
                        </button>
                    </div>
                )}

                {/* Cuando el 202 ya abrió la hoja de reemplazo, un fallo de red
                    es de confirmOverwrite, no del submit: se muestra DENTRO de
                    la hoja (más abajo), no acá — acá quedaría tapado por el
                    scrim y sería invisible. */}
                {c.hasNetworkError && c.outcome?.kind !== 'already-has-key' && (
                    <p className={'ka-network-error'} role={'alert'}>
                        {t('key-access.network-error')}
                    </p>
                )}

                {c.outcome?.kind !== 'no-account' && c.outcome?.kind !== 'denied' && (
                    <p className={'ka-hint'}>{t('key-access.hint')}</p>
                )}
            </div>

            <div className={'ka-cta-bar'}>
                <Button
                    type={'primary'}
                    className={'ka-cta'}
                    disabled={!c.canSubmit}
                    loading={c.isSubmitting}
                    onClick={c.submit}
                >
                    {t('key-access.cta')}
                </Button>
            </div>

            {c.outcome?.kind === 'already-has-key' && (
                <div className={'ka-sheet-scrim'}>
                    <div
                        className={'ka-sheet'}
                        role={'dialog'}
                        aria-modal={'true'}
                        aria-labelledby={'ka-overwrite-title'}
                    >
                        <div className={'ka-sheet-grabber'} />
                        <h2 id={'ka-overwrite-title'} className={'ka-sheet-title'}>
                            {t('key-access.overwrite-title')}
                        </h2>
                        <p className={'ka-sheet-body'}>{t('key-access.overwrite-body')}</p>
                        {/* No cierra la hoja ni pierde el recordId: repetir todo
                            el submit sería el costo de sacar a la persona de acá. */}
                        {c.hasNetworkError && (
                            <p className={'ka-sheet-error'} role={'alert'}>
                                {t('key-access.overwrite-network-error')}
                            </p>
                        )}
                        <Button
                            type={'primary'}
                            className={'ka-cta'}
                            loading={c.isSubmitting}
                            onClick={c.confirmOverwrite}
                        >
                            {t('key-access.overwrite-confirm')}
                        </Button>
                        <Button className={'ka-cta ka-cta--secondary'} onClick={c.cancelOverwrite}>
                            {t('key-access.overwrite-cancel')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
